import { useNavigate } from '@tanstack/react-router';
import type { OnboardingStep } from '@orion/contracts';
import { Avatar, Button } from '@orion/ui';
import { useAuth } from '../../lib/auth.js';
import { useCompleteOnboardingStep, useOnboardingStatus } from './api.js';

const STEPS: Array<{ key: OnboardingStep; title: string; description: string }> = [
  { key: 'profile', title: 'Complete your profile', description: 'Your name and photo are already set from your account.' },
  { key: 'company', title: 'Company setup', description: 'General settings live in Administration → General & company.' },
  { key: 'configure', title: 'Configure your CRM', description: 'Lead statuses and pipeline stages ship with sensible defaults — change them any time in Administration.' },
  { key: 'first_lead', title: 'Add your first lead', description: 'Create one now, or import a list from a CSV.' },
];

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1E7A4C" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12 5 5 9-10" />
    </svg>
  );
}

/** docs section 4 "First Login" — a short, admin-configurable sequence,
 * never a forced multi-step wizard for its own sake.
 *
 * design/orion-crm-design/rendered-html/Onboarding.html's stepper shows 5
 * named steps (it adds a "Welcome" step before the 4 this product actually
 * tracks in ONBOARDING_STEPS) — that step doesn't exist in the backend's
 * completed/required-steps model, so the stepper here has 4 stops, not 5,
 * rather than fabricating a step nothing ever marks complete. Likewise the
 * design's "company" step collects industry/company-size/currency/timezone
 * fields that have no settings API behind them yet; that step keeps its
 * existing real behavior (point to Administration) instead of a form that
 * would silently discard what someone typed into it. */
export function OnboardingPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useOnboardingStatus();
  const completeStep = useCompleteOnboardingStep();

  if (isLoading || !data) return null;

  const nextStep = STEPS.find((s) => data.requiredSteps.includes(s.key) && !data.completedSteps.includes(s.key));

  if (!nextStep) return null;

  const finish = async () => {
    await completeStep.mutateAsync(nextStep.key);
    if (nextStep.key === 'first_lead') await navigate({ to: '/leads/new' });
  };

  const stepIndex = STEPS.findIndex((s) => s.key === nextStep.key);
  const requiredSteps = STEPS.filter((s) => data.requiredSteps.includes(s.key));
  const doneCount = requiredSteps.filter((s) => data.completedSteps.includes(s.key)).length;
  const upNext = requiredSteps.filter((s) => s.key !== nextStep.key && !data.completedSteps.includes(s.key));

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-ground">
      <header className="flex h-16 shrink-0 items-center gap-4 border-b border-border px-8">
        <span className="flex size-8 items-center justify-center rounded-lg bg-night">
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="6.5" cy="16" r="2" fill="#F2B25C" />
            <circle cx="12" cy="12" r="2" fill="#F2B25C" />
            <circle cx="17.5" cy="8" r="2" fill="#F2B25C" />
            <circle cx="5" cy="5" r="1.1" fill="#8A909C" />
            <circle cx="19" cy="19" r="1.1" fill="#8A909C" />
          </svg>
        </span>
        <span className="text-base font-[650] tracking-[-0.01em] text-ink">Orion</span>
        <span aria-hidden="true" className="h-5 w-px bg-border-strong" />
        <span className="text-sm text-text-secondary">Setting up {session?.tenant.name ?? 'your'} Workspace</span>
        <span className="flex-1" />
        <span className="text-[13px] tabular-nums text-text-muted">
          Step {stepIndex + 1} of {STEPS.length}
        </span>
        <button type="button" onClick={finish} className="flex h-9 items-center px-2 text-sm font-semibold text-text-secondary hover:text-ink">
          Skip for now
        </button>
        {session && <Avatar name={session.user.displayName} size="md" />}
      </header>

      <div className="flex flex-col items-center gap-3 pt-6">
        <ol aria-label="Setup progress" className="flex items-center gap-3">
          {STEPS.map((step, i) => {
            const completed = data.completedSteps.includes(step.key);
            const current = step.key === nextStep.key;
            return (
              <li key={step.key} className="flex items-center gap-3">
                {i > 0 && <span aria-hidden="true" className={`h-0.5 w-12 rounded-full ${completed || STEPS.slice(0, i).every((s) => data.completedSteps.includes(s.key)) ? 'bg-success' : 'bg-border'}`} />}
                <span className="flex items-center gap-2">
                  <span
                    className={
                      completed
                        ? 'flex size-7 items-center justify-center rounded-full bg-success-soft'
                        : current
                          ? 'flex size-7 items-center justify-center rounded-full bg-primary text-[13px] font-[650] text-white shadow-[0_0_0_4px_#DDE2FB]'
                          : 'flex size-7 items-center justify-center rounded-full border border-border-strong bg-surface text-[13px] font-semibold text-text-muted'
                    }
                  >
                    {completed ? <CheckIcon /> : i + 1}
                  </span>
                  <span className={current ? 'text-sm font-semibold text-primary-text' : 'text-sm font-medium text-text-secondary'}>{step.title.replace(/^(Complete your|Add your)\s/, '')}</span>
                </span>
              </li>
            );
          })}
        </ol>
        <p className="flex items-center gap-1.5 text-xs text-text-muted">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5M12 8h.01" />
          </svg>
          Admins choose which of these steps are required.
        </p>
      </div>

      <div className="grid flex-1 grid-cols-[1fr_720px_1fr] items-start gap-8 py-6">
        <div />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void finish();
          }}
          className="flex flex-col gap-6 rounded-2xl border border-border bg-surface p-7 shadow-card"
        >
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
              Step {stepIndex + 1} · {nextStep.title}
            </span>
            <h1 className="mt-0.5 text-[22px] leading-7 font-[650] tracking-[-0.01em] text-ink">{nextStep.title}</h1>
            <p className="text-sm text-text-muted">{nextStep.description}</p>
          </div>

          <div className="flex items-center gap-2 border-t border-divider pt-5">
            <span className="flex-1" />
            <Button type="submit" size="lg" loading={completeStep.isPending}>
              {nextStep.key === 'first_lead' ? 'Create a lead' : 'Continue'}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Button>
          </div>
        </form>

        <div className="pl-8">
          <aside aria-labelledby="ob-next" className="flex w-66 flex-col gap-4 rounded-xl border border-border bg-surface p-5 shadow-card">
            <div className="flex flex-col gap-2">
              <h2 id="ob-next" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
                What&apos;s next
              </h2>
              <span className="flex items-center gap-2 text-xs text-text-secondary">
                <span className="flex h-1.5 flex-1 rounded-full bg-divider">
                  <span className="rounded-full bg-primary" style={{ width: `${(doneCount / Math.max(requiredSteps.length, 1)) * 100}%` }} />
                </span>
                {doneCount} of {requiredSteps.length} done
              </span>
            </div>
            {upNext.length > 0 ? (
              <ul className="flex flex-col gap-4">
                {upNext.map((step) => (
                  <li key={step.key} className="flex gap-3">
                    <span className="size-5.5 shrink-0 rounded-full border-[1.5px] border-border-strong" />
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-ink">{step.title}</span>
                      <span className="text-xs text-text-muted">{step.description}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-text-muted">This is the last required step.</p>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
